import React, { useEffect, useRef, useState } from 'react';

/**
 * Clean, professional custom prompt modal — replaces the browser-native
 * `window.prompt()` which shows the ugly "site.com says" URL header on
 * mobile Chrome. Renders a small centered card with a title, textarea and
 * Cancel / Confirm buttons.
 */
const ReasonPromptModal = ({
  open,
  title = 'Reason',
  placeholder = '',
  helperText = '',
  confirmLabel = 'Submit',
  cancelLabel = 'Cancel',
  onCancel,
  onConfirm,
}) => {
  const [value, setValue] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setTimeout(() => ref.current?.focus(), 60);
    }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const v = (value || '').trim();
    if (!v) return;
    onConfirm?.(v);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={() => onCancel?.()}
      data-testid="reason-prompt-backdrop"
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="reason-prompt-modal"
      >
        <div className="px-5 pt-4 pb-2 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800 text-base">{title}</h3>
          {helperText && (
            <p className="text-[11.5px] text-slate-500 mt-0.5">{helperText}</p>
          )}
        </div>
        <div className="p-4">
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100"
            data-testid="reason-prompt-input"
          />
        </div>
        <div className="px-4 pb-4 flex items-center justify-end gap-2">
          <button
            onClick={() => onCancel?.()}
            className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium"
            data-testid="reason-prompt-cancel"
          >{cancelLabel}</button>
          <button
            onClick={submit}
            disabled={!value.trim()}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-sm font-semibold"
            data-testid="reason-prompt-confirm"
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default ReasonPromptModal;
